import { useEffect, useRef, useCallback } from 'react';
import { debugLogger } from '@/utils/debugLogger';

interface UseScrollToTopProps {
  onScrollToTop: () => void;
  threshold?: number;
  debounceMs?: number;
  disabled?: boolean;
}

export const useScrollToTop = ({ 
  onScrollToTop, 
  threshold = 50, 
  debounceMs = 1000,
  disabled = false 
}: UseScrollToTopProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastTriggerRef = useRef<number>(0);
  const isInitializedRef = useRef<boolean>(false);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || disabled) return;

    // Evita ativação durante os primeiros 3 segundos após inicialização
    if (!isInitializedRef.current) return;
    
    const now = Date.now();
    const timeSinceLastTrigger = now - lastTriggerRef.current;
    
    // Cooldown de 2 segundos entre triggers
    if (timeSinceLastTrigger < 2000) return;

    // Verifica se está próximo do topo
    if (container.scrollTop <= threshold) {
      debugLogger.debug(`[useScrollToTop] Triggered - scrollTop: ${container.scrollTop}, threshold: ${threshold}`);
      
      // Debounce para evitar múltiplas chamadas
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        lastTriggerRef.current = Date.now();
        onScrollToTop();
      }, debounceMs);
    }
  }, [onScrollToTop, threshold, debounceMs, disabled]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Delay de 3 segundos antes de ativar o scroll detection
    const initTimer = setTimeout(() => {
      isInitializedRef.current = true;
      debugLogger.debug('[useScrollToTop] Scroll detection activated after 3s delay');
    }, 3000);

    container.addEventListener('scroll', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(initTimer);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleScroll]);

  return scrollContainerRef;
};